import { useEffect, useRef, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useHistory } from "react-router-dom";
import Table from "components/__new/common/Table";
import DownloadAsCSV from "components/__new/common/DownloadAsCSV";
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import useUrlQueryParams from "hooks/useUrlQueryParams";
import Pagination from "components/__new/common/pagination";
import processParams from "utils/processParams";
import Maybe from "components/__new/common/Maybe";
import useUrlNavigation from "hooks/useUrlNavigation";
import { TableContent } from "components/__new/common/TableContent";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import CurrencyFormat from "utils/currencyFormat";
import DateFormat, { DaysLeft } from "utils/dateFormat";
import { renderStatusColor } from "utils/statusColor";
import DropdownNew from "utils/dropdown";
// import FreeTrialInlineCountdown from "components/__new/payrollFreeTrial/FreeTrialInlineCountdown";
import FreeTrialOfferModal from "components/__new/payrollFreeTrial/FreeTrialOfferModal";
// import { PayRemittances } from "components/__new/payroll/runPayroll/PayRemittances";
import { transformPayrollName } from "utils/payroll/transformPayrollName";
import * as payrollAction from "store/entities/payroll/runPayroll/action";
import IconEmptyBox from "assets/images/icons/project-icons/empty-box.png";
import { toast } from "react-toastify";
import { ReactComponent as IconArrowRight } from "assets/images/icons/project-icons/arrow-right.svg";
import PayrollChart from "components/__new/payroll/runPayroll/PayrollChart";
// import FreeTrialInlineCountdown from "components/__new/payrollFreeTrial/FreeTrialInlineCountdown";
// import useEmployerProducts from "hooks/useEmployerProducts";
import CreatePayroll from "components/__new/payroll/runPayroll/CreatePayroll";
import moment from "moment";
import { DeletePayroll } from "../payroll-history/DeletePayroll";
import { truncate } from "utils/truncate";


const Index = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const history = useHistory();
    const {urlQuery} = useUrlNavigation();
    const {urlQueryParams} = useUrlQueryParams();

    const {
        payrollsLoading, 
        payrolls,
        payrollTransactionsDownload,
        payrollTransactionsDownloadLoading,
        payrollDeleteLoading, 
        payrollDelete,
        payrollAnalytics,
        payrollAnalyticsLoading,
    } = useSelector((s) => s.entities.runPayroll);


    const downloadTransactionsAsCSVRef = useRef();
    const printComponentRef = useRef();

    const [isOpenCreatePayroll, setIsOpenCreatePayroll] = useState(false)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedPayroll, setSelectedPayroll] = useState(null);
    
    const [listParams, setListParams] = useState({
        "page": 1,
        "limit": 20,
    });

    const customQuery = "status=rejected&status=completed&status=draft&status=in-review&status=approved&status=processing";
    const [downloadParams, setDownloadParams] = useState({
        filename: "",
    });
    const [sortOptions, setSortOptions] = useState([
        {
            title: <>Date Created <FontAwesomeIcon icon="arrow-up" className="ml-2 mt-0.5" /></>,
            link: urlQuery("sort=createdAt&order=asc"),
            data: {sort: 'createdAt', order: 'asc'},
            active: false,
        },
        {
            title: <>Date Created <FontAwesomeIcon icon="arrow-down" className="ml-2 mt-0.5" /></>,
            link: urlQuery("sort=createdAt&order=desc"),
            data: {sort: 'createdAt', order: 'desc'},
            active: false,
        },
    ]);
    
    const { dashboardLoading, bulkPayMetricsLoading, bulkPayAggregatesLoading } = useSelector((s) => s.entities.dashboard);

    
    const paginationData = useMemo(() => {
        if (!payrolls?.data) return {};
        
        const { data, ...paginationInfo } = payrolls;
        return paginationInfo;
    }, [payrolls])
    
        
    const deletePayroll = async () => {
        dispatch(payrollAction.deletePayroll(selectedPayroll?.id));
    }
    
    const getPayrolls = () => {
        let params = {...listParams};

        params = processParams(urlQueryParams, params, "page", "number", 1, "page");
        params = processParams(urlQueryParams, params, "limit", "number", 20, "limit");
        params = processParams(urlQueryParams, params, "search", "string", "", "search");
        params = processParams(urlQueryParams, params, "sort", "string", "", "sortBy");
        params = processParams(urlQueryParams, params, "order", "string", "", "sortOrder");
        params = processParams(urlQueryParams, params, "startDate", "string", "", "startDate");
        params = processParams(urlQueryParams, params, "endDate", "string", "", "endDate");

        const sortValue = urlQueryParams["sort"];
        const orderValue = urlQueryParams["order"];
        if (sortValue && orderValue) {
            const newSortOptions = sortOptions?.map((sortOption) => {
                if (sortOption?.data?.sort === sortValue && sortOption?.data?.order === orderValue) {
                    return {...sortOption, active: true};
                }
                else {
                    return {...sortOption, active: false};
                }
            })
            setSortOptions(newSortOptions);
        }

        setListParams(params);
        // dispatch(payrollAction.getPayrolls(params));
        dispatch(payrollAction.getPayrolls(params, customQuery));
    }


    const downloadPayrollTransactions = (payroll) => {
        if (payroll){
            toast("Downloading transactions");

            setDownloadParams({
                ...downloadParams,
                filename: (transformPayrollName(payroll?.name) || "file") + ".csv",
            })

            const params = {
                page: 1,
                limit: 2000,
            }

            dispatch(payrollAction.downloadPayrollTransactions(payroll?.id, params)).then(() => {
                downloadTransactionsAsCSVRef.current?.downloadData();
            });
        }
    }
    
    const previousPayroll = useMemo(() => {
        if (!payrollAnalytics?.data || payrollAnalytics?.data?.length === 0) return null;
        
        const allPayrolls = payrollAnalytics.data;
        if (allPayrolls.length === 0) return null;
        
        const payroll = allPayrolls.reduce((latestPayroll, currentPayroll) => {
            const currentDate = new Date();
            const currentPayrollDate = new Date(currentPayroll?.payroll_date_range?.end_date);
            const latestPayrollDate = new Date(latestPayroll?.payroll_date_range?.end_date);
        
            if (currentPayrollDate < currentDate) {
                return !latestPayroll || currentPayrollDate > latestPayrollDate ? currentPayroll : latestPayroll;
            }
        
            return latestPayroll;
        }, null);        
        return payroll || null;
    }, [payrollAnalytics]);
    
    const upcomingPayroll = useMemo(() => {
        if (!payrollAnalytics?.data || payrollAnalytics?.data?.length === 0) return null;
        const payrollsToBeDisbursed = payrollAnalytics.data.filter(data => data.status.toLowerCase() !== "rejected");
        if (payrollsToBeDisbursed.length === 0) return null;
        
        const currentMonth = moment.utc().month();
        const payrollsInCurrentMonth = payrollsToBeDisbursed.filter((payroll) => {
            const payrollsMonth = moment.utc(payroll?.payroll_date_range?.end_date).month();
            return payrollsMonth >= currentMonth;
        });
        
        if (payrollsInCurrentMonth.length === 0) return null;
        
        const payroll = payrollsInCurrentMonth.reduce((oldestPayroll, currentPayroll) => {
            const currentDate = new Date();
            const currentPayrollDate = new Date(currentPayroll?.payroll_date_range?.end_date);
            const oldestPayrollDate = new Date(oldestPayroll?.payroll_date_range?.end_date);
        
            if (currentPayrollDate < currentDate) {
                return oldestPayroll;
            }
        
            return !oldestPayroll || currentPayrollDate < oldestPayrollDate ? currentPayroll : oldestPayroll;
        }, null);        
        return payroll || null;
    }, [payrollAnalytics])


    // const getPayDate = (schedule) => { 
    //     if(schedule.payroll_frequency === "MONTHLY") {
    //         return `${schedule?.day_of_month}/${schedule?.month?.toString().padStart(2, "0")}/${schedule?.year}`
    //     } else {
    //         return "-"
    //     }
    // }


    // const payPayrollRemittances = (payroll) => {
    //     setPayroll(payroll);
    //     setIsOpenPayRemittances(true);
    // }
    
    const handleViewPayroll = (id) => {
        history.push(`/payroll/${id}/run`);
    }   
     
    useEffect(() => {
        if (payrollDelete?.id) {
            dispatch(payrollAction.resetDeletePayroll());
            toast.success("Delete payroll successfully");
            setIsDeleteModalOpen(false);     
            history.push({ ...urlQueryParams, page: 1 });
        }
    }, [payrollDelete])


    useEffect(() => {
        getPayrolls();
        // eslint-disable-next-line
    }, [location]);
    
    useEffect(() => {
        dispatch(payrollAction.getPayrollAnalytics());
    }, []);

    return (
        <div>

            <DynamicHeadTag
                headerText="Payroll"
                headerDescription=""
            />

            <FreeTrialOfferModal />

            <CreatePayroll
                isOpen={isOpenCreatePayroll}
                setIsOpen={setIsOpenCreatePayroll}
            />
            
            <DeletePayroll 
                loading={payrollDeleteLoading}
                isOpen={isDeleteModalOpen}
                setIsOpen={setIsDeleteModalOpen}
                onProceed={deletePayroll}
            />

            {/* 
            <PayRemittances
                isOpen={isOpenPayRemittances}
                setIsOpen={setIsOpenPayRemittances}
                payroll={payroll}
            />
            */}

            <div className="lg:flex">
                <div className="w-full lg:w-1/2">
                    <div className="page-title">
                        All Payroll
                    </div>
                </div>
                <div className="w-full lg:w-1/2 flex mt-4 lg:mt-0 lg:justify-end space-x-3">
                    <button onClick={() => setIsOpenCreatePayroll(true)} className="btn btn-md btn-ep-primary">
                        <FontAwesomeIcon icon="plus" className="mr-2" />
                        Create New Payroll
                    </button>
                </div>
            </div>

            <div className="my-6 box py-8">

                <div className="mb-2 text-[#718096]">Payroll</div>
                <div className="mb-2 font-semibold text-lg">Payroll Analytics</div>
                <hr />

                <div className="flex flex-col lg:flex-row justify-between">
                    <div className="dashboard-chart">
                        <Maybe condition={payrollAnalytics?.data?.length > 0}>
                            <PayrollChart
                                loading={payrollAnalyticsLoading}
                                data={payrollAnalytics?.data}
                            />
                        </Maybe>
                        <Maybe condition={payrollAnalytics?.data?.length === 0}>
                            <div style={{padding: '5rem'}}>
                                <div className='text-center text-[#4A5564] border rounded-xl p-5'>
                                    <div className='my-2'>No Data Available Yet.</div>
                                    <div className='my-2'>Your data will appear here once you complete a <br /> transaction.</div>
                                </div>
                            </div>
                        </Maybe>
                    </div>
                    <div className="mt-6 sm:mt-0 p-2 dashboard-summary"> 
                        <div className="flex flex-col gap-6">
                            <div className="rounded-xl p-6 border">
                                <Maybe condition={!previousPayroll}>
                                    <div className="flex space-x-3 justify-between">
                                        <div>
                                            <div className="text-[#1A202C] text-sm my-1">Previous Payroll</div>
                                            <div className="font-medium text-2xl my-1">{CurrencyFormat(0)}</div>
                                        </div>
                                        <div>
                                            -
                                        </div>
                                    </div>
                                </Maybe>
                                <Maybe condition={previousPayroll}>
                                    <div className="flex justify-between mb-2">
                                        <div style={{ width: "48%" }}>
                                            <div className="text-[#1A202C] text-sm my-1">Previous Payroll</div>
                                            <div className="font-medium text-2xl my-1"  style={{ wordBreak: "break-word" }}>
                                                {CurrencyFormat(previousPayroll?.transaction_metrics?.total_net_pay)}    
                                            </div>
                                        </div>
                                        <div className="flex flex-col items-end" style={{ width: "48%" }}>
                                            <div className="text-[#1A202C] my-1 text-sm">
                                                {DateFormat(previousPayroll?.payroll_date_range?.end_date) || "-"}
                                            </div>
                                            <div className={`label ${renderStatusColor(previousPayroll?.status)} my-1 capitalize`}>
                                                {previousPayroll?.status?.toLowerCase()}
                                            </div>
                                        </div>
                                    </div>
                                    <button 
                                        onClick={() => handleViewPayroll(previousPayroll.id)} 
                                        className="w-full border rounded-lg py-3 hover:shadow-box-shadow"
                                    >
                                        View payroll
                                    </button>
                                </Maybe>
                            </div>

                            <div className="rounded-xl p-6 border">
                                <Maybe condition={!upcomingPayroll}>
                                    <div className="flex space-x-3 justify-between">
                                        <div>
                                            <div className="text-[#1A202C] text-sm my-1">Upcoming Payroll</div>
                                            <div className="font-medium text-2xl my-1">{CurrencyFormat(0)}</div>
                                        </div>
                                        <div>
                                            -
                                        </div>
                                    </div>
                                </Maybe>
                                <Maybe condition={upcomingPayroll}>
                                    <div className="flex justify-between mb-2">
                                        <div style={{ width: "48%" }}>
                                            <div className="text-[#1A202C] text-sm my-1">Upcoming Payroll</div>
                                            <div className="font-medium text-2xl my-1"  style={{ wordBreak: "break-word" }}>{CurrencyFormat(upcomingPayroll?.transaction_metrics?.total_net_pay)}</div>
                                        </div>
                                        <div className="flex flex-col items-end" style={{ width: "48%" }}>
                                            <div className="text-[#1A202C] my-1 text-sm">
                                                {DateFormat(upcomingPayroll?.payroll_date_range?.end_date) || "-"}
                                            </div>
                                            <div className={`label ${renderStatusColor(upcomingPayroll?.status)} my-1 capitalize`}>
                                                {upcomingPayroll?.status?.toLowerCase()}
                                            </div>
                                        </div>
                                    </div>
                                    <button className="w-full border rounded-full py-1 bg-[#FFFAEB] text-[#B54708] hover:shadow-box-shadow flex gap-6 items-center justify-center" onClick={() => handleViewPayroll(upcomingPayroll.id)}>
                                        <span className="bg-white rounded-full px-3 py-1">
                                            {DaysLeft(upcomingPayroll?.payroll_date_range?.end_date || "")}
                                        </span>
                                        View payroll &rarr;
                                    </button>
                                </Maybe>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="flex space-x-5 ml-5 px-5 mt-4 items-center">
                    <div className="text-[#9291A5] flex items-center space-x-2">
                        <div className="w-3 h-3 bg-[#00535C] rounded-full mr-2"></div>
                        Gross Salary
                    </div>
                    <div className="text-[#9291A5] flex items-center space-x-2">
                        <div className="w-3 h-3 bg-[#197681] rounded-full mr-2"></div>
                        Taxes
                    </div>
                    <div className="text-[#9291A5] flex items-center space-x-2">
                        <div className="w-3 h-3 bg-[#8CD2DA] rounded-full mr-2"></div>
                        Net Pay
                    </div>
                    <div className="text-[#9291A5] flex items-center space-x-2">
                        <div className="w-3 h-3 bg-[#B4C6C8] rounded-full mr-2"></div>
                        Deductions
                    </div>
                </div>
            </div>

            <div>
                <Link to="/payroll/history" className="flex justify-end items-center text-ep-primary-600">
                    View Payroll History
                    <IconArrowRight className="ml-2"/>
                </Link>
            </div>

            <div className="mt-6 box">
                <div className="flex flex-wrap justify-between">
                    <div className="max-w-sm flex-grow sm:mr-6">
                        <div className="h-10 flex font-medium text-lg lg:items-center">
                            Payroll
                            {/* <span className="text-ep-secondary">(Pending Approvals)</span> */}
                        </div>
                    </div>
                    {/* <div className="flex flex-wrap space-x-3 sm:space-x-6">
                        <div>
                            <SortBy options={sortOptions} title=" " firstOption="Sort by" />
                        </div>
                        
                        <div>
                            <DownloadAsCSV
                                ref={downloadAsCSVRef}
                                tooltip="Download payroll drafts"
                                filename="Payroll drafts.csv"
                                columns={[
                                    { label: "Name", key: "name" },
                                    { label: "Pay Freq.", key: "schedule.payroll_frequency"},
                                    { label: "Total Net Pay", key: "transaction_metrics.total_net_pay" },
                                    { label: "Deductions (Tax inc.)", key: "transaction_metrics.total_deductions" },
                                    { label: "Payroll Type", key: "type" },
                                    { label: "Payroll Cost", key: "transaction_metrics.total_cost" },
                                    { label: "Payroll status", key: "status" },
                                    { label: "Date Created", key: "createdAt" },
                                ]}
                                click={() => {
                                    dispatch(payrollAction.downloadGetPayrolls({...listParams, page: 1, limit: 2000}, customQuery)).then(() => {
                                        downloadAsCSVRef.current?.downloadData();
                                    });
                                }}
                                loading={payrollsDownloadLoading}
                                response={transformDownloadData(payrollsDownload?.data)}
                            />
                        </div>

                        <div>
                            <Print component={printComponentRef} />
                        </div>
                    </div> */}
                </div>

                <div className="mt-6">
                    <div ref={printComponentRef}>

                        <div className="mb-4 text-xl hidden print:block">
                            Payroll History
                        </div>
                        <Table>
                            <Table.Head>
                                <th>Payroll Name</th>
                                <th>Frequency</th>
                                <th>Total Net Pay </th>
                                <th>Deductions</th>
                                <th>Cost</th>
                                <th>Pay Date</th>
                                <th>Status</th>
                                <th></th>
                            </Table.Head>
                            <Table.Body>

                                <TableContent
                                    loading={payrollsLoading}
                                    loadingText="Loading payrolls"
                                    data={payrolls?.data || []}
                                    total={payrolls?.data?.length}
                                    colspan={8}
                                    emptyUI={<>
                                        <div className="py-20 mx-auto text-center text-base">
                                            <div className="flex justify-center">
                                                <img src={IconEmptyBox} alt="Empty" className="h-48" />
                                            </div>
                                            <div className="text-[#101828] font-medium">
                                                No Payroll Created Yet
                                            </div>
                                            <div className="mt-1 text-[#4A5564]">
                                                Once you create a payroll, it will appear here
                                            </div>
                                        </div>
                                    </>}
                                />

                                {payrollsLoading === false && payrolls?.data?.map((payroll) =>
                                    <tr key={payroll?.id}>
                                        <td className="capitalize">
                                            <Link 
                                                /* to={"/payroll/" + payroll?.id + "/run"}  */
                                                to={`/payroll/${payroll?.id}/${payroll?.status === "approved" ? "finalize": "run"}`}
                                                className="font-medium hover:underline"
                                            >
                                                {/* {transformPayrollName(payroll?.name) || "-"} */}
                                                {truncate(payroll?.name?.includes("_") ? transformPayrollName(payroll?.name || "-") : payroll?.name, 30)}
                                                
                                            </Link>
                                        </td>
                                        <td className="text-fade-3 capitalize">
                                            {payroll?.schedule?.payroll_frequency?.replaceAll("_", "-")?.toLowerCase() || "-"}
                                        </td>
                                        <td className="text-fade-3">
                                            {CurrencyFormat(payroll?.transaction_metrics?.total_net_pay) || "-"}
                                        </td>
                                        <td className="text-fade-3">
                                            {CurrencyFormat(payroll?.transaction_metrics?.total_deductions) || "-"}
                                        </td>
                                        {/* <td className="text-fade-3">
                                            {payroll?.type || "-"}
                                        </td> */}
                                        <td className="text-fade-3">
                                            {CurrencyFormat(payroll?.transaction_metrics?.total_cost) || "-"}
                                        </td>
                                        <td className="text-fade-3">
                                            {DateFormat(payroll?.payroll_date_range?.end_date) || "-"}
                                        </td>
                                        <td className="capitalize">
                                            <label className={"label " + renderStatusColor(payroll?.status)}>
                                                {payroll?.status}
                                            </label>
                                        </td>
                                        <td>
                                            <DropdownNew>
                                                <></>
                                                <div>
                                                    {/* <Link to={"/payroll/history/" + payroll?.id} className="dropdown-item dropdown-item-icon">
                                                        View details
                                                    </Link> */}
                                                    <Maybe condition={payroll?.status === "approved"}>
                                                        <Link to={"/payroll/" + payroll?.id + "/finalize"} className="dropdown-item dropdown-item-icon">
                                                            Go to payrun
                                                        </Link>
                                                    </Maybe>
                                                    <Maybe condition={payroll?.status !== "approved"}>
                                                        <Link to={"/payroll/" + payroll?.id + "/run"} className="dropdown-item dropdown-item-icon">
                                                            Go to payrun
                                                        </Link>
                                                    </Maybe>
                                                    <div onClick={() => downloadPayrollTransactions(payroll)} className="dropdown-item dropdown-item-icon">
                                                        Download CSV
                                                    </div>
                                                    <Maybe condition={payroll?.status === "draft"}>
                                                        <div 
                                                            className="dropdown-item dropdown-item-icon" 
                                                            onClick={() => {
                                                                setSelectedPayroll(payroll);
                                                                setIsDeleteModalOpen(true);
                                                            }}
                                                        >
                                                            Delete
                                                        </div>
                                                    </Maybe>
                                                </div>
                                            </DropdownNew>
                                        </td>
                                    </tr>
                                )}

                            </Table.Body>
                        </Table>

                        <Maybe condition={payrollsLoading === false && payrolls?.data?.length > 0}>
                            <div className="mt-6">
                                <Pagination data={paginationData} limit={paginationData?.limit} />
                            </div>
                        </Maybe>

                    </div>
                </div>
            </div>
        
            <DownloadAsCSV 
                filename={downloadParams.filename}  
                ref={downloadTransactionsAsCSVRef}
                columns={[
                    { label: "Employee name", key: "employee_name" },
                    { label: "Employee number", key: "employee_number" },
                    { label: "Workdays", key: "days_worked"},
                    { label: "Gross Pay", key: "gross_pay" },
                    { label: "Net Pay", key: "net_pay" },
                    { label: "On-demand Pay", key: "on_demand_pay" },
                    { label: "Manual Deductions", key: "manual_deductions" },
                    { label: "Manual Bonus", key: "manual_bonuses" },
                    { label: "Custom", key: "" },
                    { label: "Pension", key: "pension_deduction.employee_amount" },
                    { label: "NHF", key: "nhf" },
                    { label: "NSITF", key: "nsitf" },
                    { label: "ITF", key: "itf" },
                    { label: "Status", key: "status" },
                ]}
                loading={payrollTransactionsDownloadLoading}
                response={payrollTransactionsDownload?.data || []}
                classname="hidden"
            />
        </div>
    )
}

export default Index;